import { Check, Minus, Plus, Phone, Star } from "lucide-react";
import { GoogleRatingLine } from "@/components/GoogleRatingLine";
import { PriceGuarantee } from "@/components/PriceGuarantee";
import { MeshPhoto } from "@/components/MeshPhoto";
import { fmt } from "@/components/swing_doors/SwingSteps";

export const INSTALL_DAYS = 7;
export const PHONE = { href: "tel:+18555004416", label: "(855) 500-4416" };

// Door + mesh come from the pricebook catalog, so prices always match /swinging-door-quote.
export const PACKAGES = [
  { id: "classic", tier: "Good", name: "Classic", model: "swing_maverick", upgrade: null,
    bullets: ["Diamond-pattern grille, a timeless look", "Standard screen mesh included", "Any of 4 frame colors"] },
  { id: "popular", tier: "Better", name: "Most Popular", model: "swing_craftsman", upgrade: "swing_upgrade_petscreen", featured: true,
    bullets: ["Craftsman vertical-bar design", "PetScreen mesh: 7x stronger for pets & kids", "Any of 4 frame colors"] },
  { id: "premium", tier: "Best", name: "Premium", model: "swing_protecto-full", upgrade: "swing_upgrade_solar",
    bullets: ["Protecto Full: full-height decorative grille", "Solar Screen blocks up to 80% of sun heat & glare", "Any of 4 frame colors"] },
];

export function Hero() {
  return (
    <section className="space-y-3" data-testid="swing-pkg-hero">
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-zinc-900 leading-tight">
        Custom Swinging Screen Doors, Measured &amp; Installed in {INSTALL_DAYS} Days
      </h1>
      <p className="text-zinc-600 text-base">Every price includes the door and professional installation.</p>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 pt-1">
        <GoogleRatingLine testid="swing-pkg-rating" />
        <a href={PHONE.href} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#207024]" data-testid="swing-pkg-call">
          <Phone className="w-4 h-4" /> Call {PHONE.label}
        </a>
      </div>
      <div className="max-w-xl"><PriceGuarantee testid="swing-pkg-guarantee" /></div>
    </section>
  );
}

function PackageCard({ p, model, upgrade, selected, onChoose }) {
  const total = model.price + (upgrade?.price || 0);
  return (
    <div className={`relative rounded-2xl border-2 bg-white p-4 flex flex-col transition-[border-color,box-shadow,transform] duration-200 ${
      p.featured ? "border-[#207024] shadow-xl md:-translate-y-2" : selected ? "border-[#207024]" : "border-zinc-200"}`}
      data-testid={`swing-pkg-card-${p.id}`}>
      {p.featured && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#f5a623] text-white text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full flex items-center gap-1 shadow">
          <Star className="w-3 h-3 fill-white" /> Most Popular
        </span>
      )}
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-zinc-500">{p.tier}</div>
      <div className="text-xl font-bold text-zinc-900">{p.name}</div>
      <div className="text-xs text-zinc-500">{model.name} door{upgrade ? ` + ${upgrade.name.replace(" Upgrade (Swinging Door)", "")}` : ", standard screen"}</div>
      <div className="h-44 my-3 flex items-center justify-center bg-zinc-50 rounded-xl">
        <img src={model.image_url} alt={model.name} className="h-full w-auto object-contain" />
      </div>
      <ul className="space-y-1.5 text-sm text-zinc-700 flex-1">
        {p.bullets.map((b) => (
          <li key={b} className="flex gap-2"><Check className="w-4 h-4 text-[#207024] shrink-0 mt-0.5" />{b}</li>
        ))}
      </ul>
      <div className="mt-3">
        <div className="text-3xl font-bold text-zinc-900" data-testid={`swing-pkg-price-${p.id}`}>{fmt(total)}</div>
        <div className="text-[11px] text-zinc-500">per door, installed · plus tax</div>
      </div>
      <button onClick={onChoose} data-testid={`swing-pkg-choose-${p.id}`}
        className={`mt-3 h-12 rounded-xl font-bold transition-colors ${p.featured || selected ? "bg-[#207024] hover:bg-[#1a5c1d] text-white" : "bg-white border-2 border-[#207024] text-[#207024] hover:bg-green-50"}`}>
        Choose {p.name}
      </button>
    </div>
  );
}

export function PackageCards({ catalog, selectedId, onChoose, onShowAll }) {
  const byKey = (list, k) => list.find((x) => x.key === k);
  return (
    <section className="space-y-4">
      <div className="grid gap-5 md:grid-cols-3 pt-3" data-testid="swing-pkg-cards">
        {PACKAGES.map((p) => {
          const model = byKey(catalog.models, p.model);
          if (!model) return null;
          return <PackageCard key={p.id} p={p} model={model} upgrade={p.upgrade ? byKey(catalog.upgrades, p.upgrade) : null}
            selected={selectedId === p.id} onChoose={() => onChoose(p)} />;
        })}
      </div>
      <div className="text-center">
        <button onClick={onShowAll} className="text-sm font-semibold text-[#207024] underline underline-offset-4" data-testid="swing-pkg-show-all">
          Prefer a different style? See all {catalog.models.length} styles
        </button>
      </div>
    </section>
  );
}

export function StepQtyMesh({ qty, setQty, upgrades, upgradeKey, setUpgradeKey, showMesh }) {
  const btn = "h-14 w-14 rounded-full border-2 border-zinc-200 bg-white flex items-center justify-center active:scale-95 transition-transform";
  const options = [{ key: null, name: "Standard", price: 0 }, ...upgrades.map((u) => ({ ...u, name: u.name.replace(" Upgrade (Swinging Door)", "") }))];
  const meshKind = { swing_upgrade_petscreen: "petscreen", swing_upgrade_solar: "solar" }[upgradeKey];
  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-zinc-900">How many doors?</h2>
        <div className="flex items-center justify-center gap-6">
          <button className={btn} onClick={() => setQty(Math.max(1, qty - 1))} data-testid="swing-qty-minus" aria-label="Fewer doors"><Minus className="w-5 h-5" /></button>
          <div className="text-5xl font-bold text-[#207024] w-16 text-center" data-testid="swing-qty-value">{qty}</div>
          <button className={btn} onClick={() => setQty(Math.min(20, qty + 1))} data-testid="swing-qty-plus" aria-label="More doors"><Plus className="w-5 h-5" /></button>
        </div>
        <p className="text-center text-sm text-zinc-500">We measure every opening in person, so no need to know sizes.</p>
      </div>
      {showMesh && (
        <div className="space-y-3" data-testid="swing-mesh-switch">
          <h3 className="text-lg font-bold text-zinc-900">Screen mesh</h3>
          <div className="grid grid-cols-3 gap-2">
            {options.map((o) => (
              <button key={o.key || "standard"} onClick={() => setUpgradeKey(o.key)} data-testid={`swing-mesh-${o.key || "standard"}`}
                className={`rounded-xl border-2 px-2 py-3 text-center transition-[border-color,background-color] ${upgradeKey === o.key ? "border-[#207024] bg-green-50" : "border-zinc-200 bg-white"}`}>
                <div className="text-sm font-bold text-zinc-900">{o.name}</div>
                <div className="text-xs text-[#207024] font-semibold">{o.price ? `+${fmt(o.price)}` : "Included"}</div>
              </button>
            ))}
          </div>
          {meshKind && <MeshPhoto kind={meshKind} />}
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full h-12 rounded-lg border-2 border-zinc-200 px-4 focus:border-[#207024] focus:outline-none";
const formatPhone = (raw) => {
  const d = raw.replace(/\D/g, "").slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

export function StepContactLite({ c, set, submitting, onSubmit }) {
  return (
    <div className="space-y-5 max-w-xl">
      <h2 className="text-2xl font-bold text-zinc-900">Lock in your price and install date</h2>
      <div className="grid gap-3">
        <input className={inputCls} placeholder="First name" value={c.name} onChange={(e) => set.name(e.target.value)} autoComplete="given-name" data-testid="swing-contact-name" />
        <input className={inputCls} placeholder="Mobile phone (555) 123-4567" type="tel" inputMode="tel" maxLength={14} value={c.phone}
          onChange={(e) => set.phone(formatPhone(e.target.value))} autoComplete="tel" data-testid="swing-contact-phone" />
      </div>
      <div className="space-y-3">
        <GoogleRatingLine testid="swing-contact-rating" />
        <PriceGuarantee testid="swing-contact-guarantee" />
      </div>
      <button onClick={onSubmit} disabled={submitting} data-testid="swing-submit-btn"
        className="w-full h-14 rounded-xl bg-[#207024] hover:bg-[#1a5c1d] text-white text-lg font-bold shadow-lg disabled:opacity-60 transition-colors">
        {submitting ? "Sending…" : "Lock In My Price"}
      </button>
      <p className="text-[11px] text-zinc-500 text-center">By continuing you agree to receive a text with your quote. Reply STOP to opt out.</p>
    </div>
  );
}
